import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApprovalCard } from "@/components/ApprovalCard";
import { ChatInput, ChatInputSubmit } from "@/components/ChatInput";
import { ChatMessage } from "@/components/ChatMessage";
import { DiagnosticProposalCard } from "@/components/DiagnosticProposalCard";
import { LiveAnalysisActivity } from "@/components/LiveAnalysisActivity";
import { Button } from "@/components/ui/button";
import {
  useAnalysisSession,
  useCloseSession,
} from "@/hooks/useAnalysis";
import { useAnalysisStream } from "@/hooks/useAnalysisStream";
import { usePendingApprovals } from "@/hooks/useApprovals";
import { useCommandDispatcher, CommandResponse } from "@/hooks/useCommandDispatcher";
import { useSessionDiagnosticRuns } from "@/hooks/useDiagnosticRuns";

export function AnalysisSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const sessionQuery = useAnalysisSession(sessionId);
  const closeMutation = useCloseSession(sessionId);
  const pendingApprovalsQuery = usePendingApprovals();
  const stream = useAnalysisStream(sessionId);

  // F14: auto-kickoff cuando entramos a una sesión recién creada con
  // defer_first_turn=true (típicamente desde ProactiveAnalysisPage).
  // Solo una vez por sesión.
  const kickoffSentFor = useRef<string | null>(null);
  const kickoffFn = stream.kickoff;
  useEffect(() => {
    if (!sessionId) return;
    if (kickoffSentFor.current === sessionId) return;
    const data = sessionQuery.data;
    if (!data) return;
    const hasAssistant = data.messages.some((m) => m.role === "assistant");
    if (hasAssistant) {
      kickoffSentFor.current = sessionId;
      return;
    }
    if (data.status !== "active") return;
    kickoffSentFor.current = sessionId;
    kickoffFn();
  }, [sessionId, sessionQuery.data, kickoffFn]);

  const sessionApprovals = useMemo(() => {
    const all = pendingApprovalsQuery.data ?? [];
    return sessionId
      ? all.filter((a) => a.analysis_session_id === sessionId)
      : [];
  }, [pendingApprovalsQuery.data, sessionId]);

  const diagnosticRunsQuery = useSessionDiagnosticRuns(sessionId);

  const [commandResponses, setCommandResponses] = useState<CommandResponse[]>([]);
  const dispatch = useCommandDispatcher({
    incidentId: sessionQuery.data?.incident_id ?? null,
  });

  const handleSubmit = async (payload: ChatInputSubmit) => {
    if (payload.type === "message" && payload.content) {
      stream.sendMessage(payload.content);
      return;
    }
    if (payload.type === "command" && payload.command) {
      const response = await dispatch(payload.command, payload.args ?? []);
      setCommandResponses((prev) => [...prev, response]);
    }
  };

  if (sessionQuery.isLoading || !sessionQuery.data) {
    return <div className="text-sm text-muted-foreground">Cargando sesión…</div>;
  }

  const session = sessionQuery.data;
  const isClosed =
    session.status === "completed" || session.status === "cancelled";
  const isStreaming = stream.status === "streaming";

  return (
    <div className="grid h-[calc(100vh-4rem)] grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,30%)_1fr]">
      <div className="space-y-4 overflow-auto">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Sesión {session.trigger_type}
          </div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">
            {session.id}
          </div>
          {session.target_host && (
            <div className="mt-2 text-sm">
              Target: <span className="font-mono">{session.target_host}</span>
            </div>
          )}
          {session.incident_id && (
            <div className="text-sm">
              Incidente:{" "}
              <button
                className="font-mono text-primary underline"
                onClick={() => navigate(`/incidents/${session.incident_id}`)}
              >
                {session.incident_id}
              </button>
            </div>
          )}
          <div className="mt-2 space-y-1 text-sm">
            <div>
              Estado: <span className="font-mono">{session.status}</span>
            </div>
            <div>
              Motor: <span className="font-mono">{session.model_id}</span>
            </div>
            <div>
              Tokens:{" "}
              <span className="font-mono">{session.token_usage.total_tokens}</span>
            </div>
            {isStreaming && (
              <div className="text-xs italic text-muted-foreground">
                AROC razonando en vivo…
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => closeMutation.mutate()}
            disabled={isClosed || closeMutation.isPending}
          >
            Cerrar sesión
          </Button>
          {isStreaming && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full"
              onClick={stream.cancel}
            >
              Cancelar turno
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
        <div className="flex-1 space-y-3 overflow-auto p-4">
          {session.messages.map((m, i) => (
            <ChatMessage key={`${m.timestamp}-${i}`} message={m} />
          ))}
          <LiveAnalysisActivity
            liveTokens={stream.liveTokens}
            liveToolCalls={stream.liveToolCalls}
            status={stream.status}
            errorMessage={stream.errorMessage}
          />
          {sessionApprovals.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                Acciones propuestas
              </div>
              {sessionApprovals.map((a) => (
                <ApprovalCard key={a.id} approval={a} />
              ))}
            </div>
          )}
          {diagnosticRunsQuery.data && diagnosticRunsQuery.data.items.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-xs uppercase text-muted-foreground">
                <span className="font-semibold">Diagnósticos propuestos</span>
                <span>
                  {diagnosticRunsQuery.data.used}/{diagnosticRunsQuery.data.limit}
                </span>
              </div>
              {diagnosticRunsQuery.data.items.map((r) => (
                <DiagnosticProposalCard
                  key={r.id}
                  run={r}
                  sessionId={sessionId!}
                />
              ))}
            </div>
          )}
          {commandResponses.map((r, i) => (
            <div
              key={`${r.timestamp}-${i}`}
              className={
                r.ok
                  ? "rounded border bg-muted/40 px-3 py-2 font-mono text-xs"
                  : "rounded border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-xs text-destructive"
              }
            >
              <pre className="whitespace-pre-wrap">{r.text}</pre>
            </div>
          ))}
        </div>
        <ChatInput
          onSubmit={handleSubmit}
          disabled={isStreaming || isClosed}
          placeholder={isClosed ? "Sesión cerrada" : undefined}
        />
      </div>
    </div>
  );
}
